import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, Download, Share2 } from "lucide-react";
import { SiteShell } from "@/project/components/site-shell";
import { formatINR, getCause, getInstitution } from "@/project/data/sample";
import { campaigns, getPartner, programs } from "@/project/data/network";
import { printReceipt } from "@/project/lib/receipt";

type SuccessSearch = { id: string; amount: number; cause?: string; institution?: string; campaign?: string; org?: string; program?: string; freq?: string; receipt?: string; target?: string };

export const Route = createFileRoute("/donate/success")({
  validateSearch: (s: Record<string, unknown>): SuccessSearch => ({
    id: typeof s.id === "string" ? s.id : "DN-00000",
    amount: s.amount != null && !Number.isNaN(Number(s.amount)) ? Number(s.amount) : 1001,
    cause: typeof s.cause === "string" ? s.cause : undefined,
    institution: typeof s.institution === "string" ? s.institution : undefined,
    campaign: typeof s.campaign === "string" ? s.campaign : undefined,
    org: typeof s.org === "string" ? s.org : undefined,
    program: typeof s.program === "string" ? s.program : undefined,
    freq: s.freq === "monthly" ? "monthly" : "once",
    receipt: typeof s.receipt === "string" ? s.receipt : undefined,
    target: typeof s.target === "string" ? s.target : undefined,
  }),
  head: () => ({ meta: [{ title: "Thank you — Animal Seva" }] }),
  component: Success,
});

function Success() {
  const sp = Route.useSearch();
  const cause = sp.cause ? getCause(sp.cause) : undefined;
  const inst = sp.institution ? getInstitution(sp.institution) : cause ? getInstitution(cause.institutionId) : undefined;
  const campaign = sp.campaign ? campaigns.find((c) => c.id === sp.campaign) : undefined;
  const program = sp.program ? programs.find((p) => p.id === sp.program) : undefined;
  const org = getPartner(sp.org ?? "");

  return (
    <SiteShell>
      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <div className="rounded-3xl border border-border/60 bg-card p-10 text-center shadow-[var(--shadow-card)]">
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[oklch(0.93_0.06_155)] text-[var(--success)]">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <h1 className="mt-5 font-display text-3xl font-semibold text-[var(--maroon-deep)]">Thank you for your kindness</h1>
          <p className="mt-2 text-foreground/70">
            Your donation will help feed, heal and shelter animals in need. You will receive impact updates within 30 days.
          </p>
          <div className="mx-auto mt-8 grid max-w-md gap-3 rounded-2xl bg-[var(--cream-2)] p-5 text-left text-sm">
            <Row label="Donation ID" value={sp.id} />
            <Row label="Amount" value={formatINR(sp.amount)} />
            {cause && <Row label="Cause" value={cause.title} />}
            {campaign && <Row label="Campaign" value={campaign.title} />}
            {program && <Row label="Programme" value={program.title} />}
            {org && <Row label="Partner organisation" value={org.name} />}
            <Row label="Frequency" value={sp.freq === "monthly" ? "Monthly recurring" : "One-time"} />
            {inst && <Row label="Institution" value={inst.name} />}
            <Row label="Date / time" value={new Date().toLocaleString("en-IN")} />
          </div>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <button
              onClick={() =>
                printReceipt({
                  id: sp.id,
                  date: new Date().toLocaleDateString("en-IN"),
                  donor: "Rahul Shah",
                  amount: sp.amount,
                  purpose: campaign?.title ?? program?.title ?? cause?.title ?? "General fund",
                  beneficiary: org?.name ?? inst?.name ?? "Daan Amrut Foundation",
                  frequency: sp.freq === "monthly" ? "Monthly recurring" : "One-time",
                })
              }
              className="inline-flex items-center gap-2 rounded-full bg-[var(--maroon)] px-5 py-2.5 text-sm font-semibold text-[var(--cream)]"
            >
              <Download className="h-4 w-4" /> Download receipt
            </button>
            <button className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-5 py-2.5 text-sm font-semibold">
              <Share2 className="h-4 w-4" /> Share this cause
            </button>
            <Link to="/donor" className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-5 py-2.5 text-sm font-semibold">
              Go to dashboard
            </Link>
          </div>
          <Link to="/causes" className="mt-6 inline-block text-sm text-[var(--maroon)] underline">Explore more causes →</Link>
        </div>
      </section>
    </SiteShell>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-foreground/70">{label}</span>
      <span className="font-medium text-foreground">{value}</span>
    </div>
  );
}